import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Cinematic neural-network style background.
 * - Renders a fixed canvas behind all content (z-index 0)
 * - Particles connect with lines when within proximity
 * - Orange (#FF6700) luminous dots, gray (#444) connecting lines
 * - Pauses when tab is hidden for battery / CPU savings
 * - Pauses when scrolled out of viewport (only top of page needs the animation)
 * - Throttled to ~30fps for lower CPU/GPU usage
 * - Skips render on /admin routes (admin UI has its own layout)
 * - On mobile (≤768px) renders a static dark gradient instead — no animation
 */
const NetworkBackground = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const dimsRef = useRef({ w: 0, h: 0, dpr: 1 });
  const location = useLocation();

  // Determine mobile once at mount — listening to resize is overkill for this.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );

  // Don't render on admin pages
  const enabled = !location.pathname.startsWith('/admin');

  useEffect(() => {
    if (!enabled || isMobile) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const setup = () => {
      // Cap DPR at 1.5 to reduce GPU load on Retina screens.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      dimsRef.current = { w, h, dpr };

      // Lower density: 1 particle per ~22k px², capped at 35 max.
      const count = Math.min(Math.floor((w * h) / 22000), 35);

      particlesRef.current = Array.from({ length: count }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.4 + 0.6,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    setup();
    const onResize = () => setup();
    window.addEventListener('resize', onResize);

    const LINK_DIST = 140;
    const ORANGE = '255, 103, 0';
    const LINE_COLOR = '120, 120, 120';

    // Throttle to ~30fps (33ms per frame) — invisible on a static-feeling
    // animation but halves the work vs uncapped 60fps.
    const FRAME_INTERVAL = 1000 / 30;
    let last = performance.now();
    let lastFrame = 0;
    let scrollPaused = false;

    const tick = (now) => {
      rafRef.current = requestAnimationFrame(tick);
      // Throttle to FRAME_INTERVAL (~30fps)
      if (now - lastFrame < FRAME_INTERVAL) return;
      lastFrame = now;

      const delta = Math.min((now - last) / 16.67, 3);
      last = now;
      const { w, h } = dimsRef.current;
      const particles = particlesRef.current;

      ctx.clearRect(0, 0, w, h);

      // Update positions
      for (const p of particles) {
        p.x += p.vx * delta;
        p.y += p.vy * delta;
        if (p.x < -10) p.x = w + 10;
        else if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        else if (p.y > h + 10) p.y = -10;
        p.phase += 0.012 * delta;
      }

      // Draw connecting lines
      ctx.lineWidth = 0.55;
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DIST) {
            const t = 1 - dist / LINK_DIST;
            // Closer lines get an orange tint, distant ones gray
            const alpha = t * 0.22;
            if (t > 0.7) {
              ctx.strokeStyle = `rgba(${ORANGE}, ${alpha * 1.4})`;
            } else {
              ctx.strokeStyle = `rgba(${LINE_COLOR}, ${alpha})`;
            }
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw particles (orange luminous dots with subtle pulse)
      for (const p of particles) {
        const pulse = 0.65 + Math.sin(p.phase) * 0.35;
        const r = p.r * (1 + pulse * 0.4);

        // Outer glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 5);
        grad.addColorStop(0, `rgba(${ORANGE}, ${0.45 * pulse})`);
        grad.addColorStop(1, `rgba(${ORANGE}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 5, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `rgba(${ORANGE}, ${0.9 * pulse})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    // Pause animation when scrolled past the first viewport — particles
    // are decorative and don't need to keep ticking when off-screen.
    const onScroll = () => {
      const shouldPause = window.scrollY > window.innerHeight * 0.9;
      if (shouldPause && !scrollPaused) {
        scrollPaused = true;
        cancelAnimationFrame(rafRef.current);
      } else if (!shouldPause && scrollPaused) {
        scrollPaused = false;
        last = performance.now();
        lastFrame = 0;
        if (!reduceMotion && !document.hidden) {
          rafRef.current = requestAnimationFrame(tick);
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    if (!reduceMotion) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      // Draw once and freeze for reduced-motion users
      tick(performance.now());
      cancelAnimationFrame(rafRef.current);
    }

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafRef.current);
      } else if (!reduceMotion && !scrollPaused) {
        last = performance.now();
        lastFrame = 0;
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, isMobile, location.pathname]);

  // Listen for breakpoint changes (e.g., user rotates device or resizes).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  if (!enabled) return null;

  // On mobile: render a static dark gradient — no canvas, no animation.
  // Keeps the aesthetic without consuming CPU/GPU continuously.
  if (isMobile) {
    return (
      <div
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 0,
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(255,103,0,0.05) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(255,103,0,0.04) 0%, transparent 50%), #000000',
        }}
        data-testid="network-bg-static"
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0, background: '#000000' }}
      data-testid="network-bg-canvas"
    />
  );
};

export default NetworkBackground;
